;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();const pendingScriptLoads = new Map<string, Promise<void>>()
/** How many loads of each script were given up on, which the next attempt's URL carries. */
const timedOutScriptLoads = new Map<string, number>()

type LoadScriptOptions = {
  id: string
  src: string
  /**
   * Gives up after this long, and the next call downloads afresh. Leave it out for an unbounded
   * wait — what reCAPTCHA wants: it bounds each submit itself and keeps a slow load shared so the
   * load can still resume after the form has fallen back (`recaptcha-score.ts`).
   */
  timeoutMs?: number
}

/**
 * Adds `<script id src>` once and resolves when it has run. Concurrent calls share one load.
 *
 * A failed element is removed. The browser never fetches a `<script>` twice, so an element left in
 * place after an error — or after a load that never settled — is dead: the next call would attach
 * its listeners to it and wait for an event that cannot come, and the visitor's second click would
 * do exactly as little as the first. Removing it makes the next call create a new element and a
 * new request.
 *
 * After a timeout that is not quite enough: the request is still pending, and Chrome attaches a new
 * element for the same URL to it rather than fetching again — the retry would wait on the very
 * request that stalled. A load that follows a timeout therefore adds `offsoo_retry=<n>` to the URL.
 * (A timed-out script that does arrive after all may still run; consumers check for the global they
 * need before loading.)
 */
export function loadExternalScript({ id, src, timeoutMs }: LoadScriptOptions): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.resolve()
  }

  const key = `${id}:${src}`
  const inFlight = pendingScriptLoads.get(key)
  if (inFlight) {
    return inFlight
  }

  const existing = document.getElementById(id) as HTMLScriptElement | null
  if (existing?.dataset.loaded === 'true') {
    return Promise.resolve()
  }

  const script = existing ?? document.createElement('script')
  let resolve!: () => void
  let reject!: (error: Error) => void
  const promise = new Promise<void>((res, rej) => {
    resolve = res
    reject = rej
  })

  let settled = false
  let timedOut = false
  let timer: number | undefined
  const settle = (error?: Error) => {
    if (settled) {
      return
    }
    settled = true
    window.clearTimeout(timer)
    // Only this load's own entry: a retry may already have replaced it.
    if (pendingScriptLoads.get(key) === promise) {
      pendingScriptLoads.delete(key)
    }
    if (error) {
      if (timedOut) {
        timedOutScriptLoads.set(key, (timedOutScriptLoads.get(key) ?? 0) + 1)
      }
      script.remove()
      reject(error)
    } else {
      resolve()
    }
  }

  script.addEventListener(
    'load',
    () => {
      script.dataset.loaded = 'true'
      settle()
    },
    { once: true },
  )
  script.addEventListener('error', () => settle(new Error(`Failed to load script: ${src}`)), { once: true })
  if (timeoutMs !== undefined) {
    timer = window.setTimeout(() => {
      timedOut = true
      settle(new Error(`Timed out loading script: ${src}`))
    }, timeoutMs)
  }

  if (!existing) {
    script.id = id
    script.async = true
    const retry = timedOutScriptLoads.get(key)
    script.src = retry ? `${src}${src.includes('?') ? '&' : '?'}offsoo_retry=${retry}` : src
    document.body.appendChild(script)
  }

  pendingScriptLoads.set(key, promise)
  return promise
}
